import { useTranslation } from "react-i18next"
import { Badge, Icon, cn } from "@nexus/ui"

/** Dashed placeholders standing in for the persona stack that will land here. */
const GHOST_AVATAR_COUNT = 5
const GHOST_AVATAR_ICON_INDEX = 2

/** Keys carry their English copy so `t()` keeps a default even with a dynamic key. */
const NEXT_STEPS = [
  {
    icon: "clapperboard",
    indexKey: "personas.waiting.step1Index",
    indexDefault: "01",
    titleKey: "personas.waiting.step1Title",
    titleDefault: "Tag creatives",
    descriptionKey: "personas.waiting.step1Description",
    descriptionDefault:
      "Every ad in your library gets labelled by the customer it speaks to.",
  },
  {
    icon: "sparkles",
    indexKey: "personas.waiting.step2Index",
    indexDefault: "02",
    titleKey: "personas.waiting.step2Title",
    titleDefault: "Cluster customer types",
    descriptionKey: "personas.waiting.step2Description",
    descriptionDefault:
      "Recurring patterns become persona suggestions with an estimated share of spend.",
  },
  {
    icon: "users",
    indexKey: "personas.waiting.step3Index",
    indexDefault: "03",
    titleKey: "personas.waiting.step3Title",
    titleDefault: "Review here",
    descriptionKey: "personas.waiting.step3Description",
    descriptionDefault:
      "You get a guided pass to accept, reject, or refine each suggestion.",
  },
] as const

export interface PersonasWaitingStateProps {
  /**
   * `page` includes the Personas title chrome (standalone / route empty).
   * `body` is the dashed panel only — used under the library header on PersonasPage.
   */
  variant?: "page" | "body"
}

/**
 * Friendly empty library — shown after queries resolve with zero ACTIVE and zero
 * SUGGESTED personas. Not a loading surface: loaders live on
 * `PersonasOnboardingLoadingState` / `PersonasLoadingState` while fetches are in flight.
 */
export function PersonasWaitingState({ variant = "page" }: PersonasWaitingStateProps) {
  const { t } = useTranslation()
  const isBody = variant === "body"

  return (
    <div
      className={cn("flex flex-col", isBody ? "gap-0" : "gap-4 p-4")}
      data-testid="personas-waiting-for-data"
      data-variant={variant}
    >
      {!isBody ? (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              {t("personas.title", "User Personas")}
            </h1>
            <Badge variant="ai" iconStart="sparkles">
              {t("personas.usedAcross", "Used across Opportunities, Briefs & Scripts")}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            {t(
              "personas.description",
              "Generate and manage audience personas that Adge uses to create more relevant opportunities, briefs, and scripts.",
            )}
          </p>
        </div>
      ) : null}

      <section
        className={cn(
          "mx-auto flex w-full flex-col items-center",
          isBody ? "max-w-none px-0 py-0" : "max-w-3xl px-2 py-6",
        )}
      >
        <div className="flex w-full flex-col items-center gap-7 rounded-md border border-dashed border-border bg-card px-6 py-10 text-center sm:px-10 sm:py-[60px]">
          <div className="flex -space-x-3.5" aria-hidden>
            {Array.from({ length: GHOST_AVATAR_COUNT }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  "flex size-11 items-center justify-center rounded-full border-2 border-dashed border-border bg-card sm:size-12",
                  index === GHOST_AVATAR_ICON_INDEX && "relative z-10",
                )}
              >
                {index === GHOST_AVATAR_ICON_INDEX ? (
                  <Icon name="users" size="lg" className="text-muted-foreground" />
                ) : null}
              </div>
            ))}
          </div>

          <div className="flex max-w-xl flex-col items-center gap-3">
            <Badge variant="slate" iconStart="clock">
              {t("personas.waiting.statusChip", "Waiting for creative data")}
            </Badge>
            <h2 className="text-[22px] font-semibold tracking-tight text-foreground">
              {t("personas.waiting.title", "No personas yet")}
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {t(
                "personas.waiting.description",
                "Adge builds personas from your tagged creatives, and there isn't enough signal yet. As soon as there is, suggestions land here for you to review.",
              )}
            </p>
          </div>

          <div className="flex w-full max-w-[660px] flex-col gap-3">
            <h3 className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("personas.waiting.stepsTitle", "What happens next")}
            </h3>
            <ol className="grid gap-3.5 sm:grid-cols-3">
              {NEXT_STEPS.map((step) => (
                <li
                  key={step.titleKey}
                  className="flex flex-col gap-1.5 rounded-md border border-border bg-muted px-4 py-4 text-left"
                >
                  <div className="flex items-center gap-2">
                    <Icon name={step.icon} size="sm" className="text-muted-foreground" />
                    <span className="font-mono text-[11px] text-muted-foreground">
                      {t(step.indexKey, step.indexDefault)}
                    </span>
                  </div>
                  <p className="text-[13px] font-semibold text-foreground">
                    {t(step.titleKey, step.titleDefault)}
                  </p>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    {t(step.descriptionKey, step.descriptionDefault)}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>
    </div>
  )
}
